import React from "react";
import {
  Gift,
  Coffee,
  Phone,
  Trash2,
  Smartphone,
  Package,
  Utensils,
  Dog,
  Car,
  Clock,
  Sparkles,
  RefreshCw,
  Navigation,
  Heart,
} from "lucide-react";

// --- The Time Row Component ---
const TimeRow = ({
  time,
  title,
  sub,
  icon: Icon,
  isLast,
  highlight = "neutral",
}) => {
  // Style Logic based on 'highlight' prop
  const styles = {
    neutral: {
      time: "text-stone-400",
      dot: "bg-white border-2 border-stone-200 text-stone-300",
      title: "text-stone-700",
      sub: "text-stone-400",
    },
    gold: {
      // THE MAGIC MOMENT
      time: "text-amber-600 font-bold",
      dot: "bg-amber-100 border-2 border-amber-300 text-amber-600 shadow-sm shadow-amber-100",
      title: "text-amber-800",
      sub: "text-amber-600/80",
    },
    red: {
      // THE DEPARTURE
      time: "text-rose-600 font-bold",
      dot: "bg-rose-500 border-2 border-rose-500 text-white shadow-md shadow-rose-200 animate-pulse",
      title: "text-rose-700",
      sub: "text-rose-600",
    },
  };

  const currentStyle = styles[highlight] || styles.neutral;

  return (
    <div className="flex gap-6 relative group">
      {/* 1. Time Anchor (Left) */}
      <div
        className={`w-16 shrink-0 text-right pt-1 font-mono text-lg tracking-tight ${currentStyle.time}`}
      >
        {time}
      </div>

      {/* 2. Visual Thread (Center) */}
      <div className="relative flex flex-col items-center">
        {/* The Icon Dot */}
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center z-10 transition-all ${currentStyle.dot}`}
        >
          <Icon className="w-4 h-4" />
        </div>
        {/* The Connecting Line */}
        {!isLast && (
          <div className="absolute top-9 bottom-[-32px] w-0.5 bg-stone-200/60"></div>
        )}
      </div>

      {/* 3. Narrative Content (Right) */}
      <div className={`flex-1 pb-10 ${isLast ? "pb-0" : ""}`}>
        <h4
          className={`text-base font-bold leading-tight flex items-center gap-2 ${currentStyle.title}`}
        >
          {title}
        </h4>
        <p
          className={`text-sm mt-1 leading-relaxed font-medium ${currentStyle.sub}`}
        >
          {sub}
        </p>
      </div>
    </div>
  );
};

// --- Main Component ---
const ChristmasTimeline = () => {
  return (
    <div className="max-w-2xl mx-auto mb-10 animate-in slide-in-from-bottom-4">
      {/* HERO HEADER */}
      <div className="text-center mb-10 pt-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-rose-50/50 to-transparent -z-10 blur-xl rounded-full opacity-60"></div>
        <h2 className="text-3xl font-extrabold text-stone-800 flex items-center justify-center gap-3">
          🎄 Christmas Morning Rhythm
        </h2>
        <p className="text-rose-500/80 text-sm font-medium mt-2 flex items-center justify-center gap-2">
          <Sparkles className="w-3 h-3" />
          A gentle plan for a joyful day
          <Sparkles className="w-3 h-3" />
        </p>
      </div>

      {/* ACT I: THE MAGIC (Warm Gold Glow) */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 ml-24">
          <Heart className="w-4 h-4 text-amber-500 fill-amber-500" />
          <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">
            Act I: The Magic
          </span>
        </div>

        {/* Warmer Container */}
        <div className="bg-gradient-to-b from-amber-50/80 to-amber-50/20 rounded-3xl p-6 sm:p-8 border border-amber-100 shadow-sm shadow-amber-100/50">
          <TimeRow
            time="06:00"
            title="Wake Up 🎄"
            sub="It begins."
            icon={Clock}
          />
          <TimeRow
            time="06:02"
            title="Nana Call (Canada) 💕"
            sub="Video link ready. Start with connection."
            icon={Phone}
          />
          <TimeRow
            time="06:15"
            title="Christmas Breakfast ☕"
            sub="Coffee & Cinnamon Rolls serving."
            icon={Coffee}
          />
          <TimeRow
            time="06:30"
            title="Gift Opening 🎁"
            sub="90 Minutes of Joy."
            icon={Gift}
            highlight="gold" // GOLD HIGHLIGHT
            isLast={true}
          />
        </div>
      </div>

      {/* ACT II: THE RESET (Clean Slate) */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 ml-24">
          <RefreshCw className="w-4 h-4 text-blue-400" />
          <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">
            Act II: The Reset
          </span>
        </div>

        <div className="bg-slate-50/60 rounded-3xl p-6 sm:p-8 border border-slate-100">
          <TimeRow
            time="08:00"
            title="Wrapping Reset ♻️"
            sub="Clear the floor. Recycle run."
            icon={Trash2}
          />
          <TimeRow
            time="09:00"
            title="Hang Time & Reset 📱"
            sub="Kids do 'Haul' TikToks. House back to baseline."
            icon={Smartphone}
          />
          <TimeRow
            time="12:00"
            title="Overnight Prep 🎒"
            sub="Bags packed for Nana & Papa's sleepover."
            icon={Package}
            isLast={true}
          />
        </div>
      </div>

      {/* ACT III: DEPARTURE (Movement) */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 ml-24">
          <Navigation className="w-4 h-4 text-emerald-500" />
          <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">
            Act III: Departure
          </span>
        </div>

        <div className="bg-emerald-50/40 rounded-3xl p-6 sm:p-8 border border-emerald-100">
          <TimeRow
            time="12:30"
            title="Road Trip Lunch 🥪"
            sub="Fed before the road."
            icon={Utensils}
          />
          <TimeRow
            time="01:30"
            title="Load the Pups 🐾"
            sub="Scout & Jackson secured."
            icon={Dog}
          />
          <TimeRow
            time="01:45"
            title="Wheels Up 🚗✨"
            sub="Hard Deadline. Target Arrival: 2:00 PM."
            icon={Car}
            highlight="red" // THE ONLY RED
            isLast={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ChristmasTimeline;
